import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Avatar } from '@/features/auth/Avatar'
import type { Rival, UserCard } from '@/features/auth/types'
import { fetchLeaderboard } from './api'
import type { CategoryRow, LeaderPeriod, LeaderRow } from './types'

type Props = {
  /** Вошёл ли человек: таблицы собраны из учётных записей, гостю в них смотреть не на что. */
  signedIn: boolean
  onSignIn: () => void
  onOpenUser: (userId: number) => void
}

/**
 * Таблицы лидеров: общий рейтинг по очкам и отдельные по клеткам
 * «соперник × размер стола».
 *
 * Вся арифметика — на сервере (App\Services\Stats\Leaderboard): и очки,
 * и доли считает он, потому что одно и то же число в двух местах
 * разъезжается на округлении. Здесь только переключатели разрезов.
 */
export function LeaderboardScreen({ signedIn, onSignIn, onOpenUser }: Props) {
  if (!signedIn) {
    return (
      <div className="lobby">
        <h2 className="hero__title">Лидеры</h2>
        <div className="card">
          <p>Таблицы собраны из учётных записей: месту нужны ник и аватарка.</p>
          <button type="button" className="primary" onClick={onSignIn}>
            войти
          </button>
        </div>
      </div>
    )
  }

  return <Tables onOpenUser={onOpenUser} />
}

function Tables({ onOpenUser }: { onOpenUser: (userId: number) => void }) {
  const [period, setPeriod] = useState<LeaderPeriod>('all')
  // Какая таблица показана: 'overall' или ключ клетки «соперник:мест».
  const [shown, setShown] = useState('overall')

  const board = useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => fetchLeaderboard(period),
  })

  if (board.isError) {
    return <p className="card">таблица не открылась — попробуйте обновить страницу</p>
  }

  if (board.data === undefined) {
    return <p className="card">считаем очки…</p>
  }

  const { overall, categories, me } = board.data

  /*
   * Выбранной клетки в этом периоде может не быть: за месяц в неё ещё
   * никто не наиграл. Тогда вместо пустого экрана показывается общий
   * рейтинг — он и есть ответ «кто сильнее за этот месяц».
   */
  const category = categories.find((one) => keyOf(one.rival, one.seats) === shown) ?? null

  return (
    <div className="lobby">
      {/*
        Название окна — как у всех экранов в модальном слое (Друзья,
        Настройки). Оно же и отступ сверху: без него первый ряд кнопок
        начинался от самого края и залезал под крестик в углу, а у
        .hero__title боковые поля под него заложены (ui.css).
      */}
      <h2 className="hero__title">Лидеры</h2>

      <div className="field field--split">
        <button
          type="button"
          className={period === 'all' ? 'primary' : ''}
          onClick={() => setPeriod('all')}
        >
          всё время
        </button>
        <button
          type="button"
          className={period === 'month' ? 'primary' : ''}
          onClick={() => setPeriod('month')}
        >
          этот месяц
        </button>
      </div>

      {/*
        Клеток без лидеров сервер не присылает вовсе (как и профиль), поэтому
        переключатель собирается из того, что пришло, без пустых кнопок.
      */}
      {categories.length > 0 && (
        <div className="field field--split">
          <button
            type="button"
            className={category === null ? 'primary' : ''}
            onClick={() => setShown('overall')}
          >
            общий
          </button>
          {categories.map((one) => (
            <button
              key={keyOf(one.rival, one.seats)}
              type="button"
              className={
                category?.rival === one.rival && category.seats === one.seats ? 'primary' : ''
              }
              onClick={() => setShown(keyOf(one.rival, one.seats))}
            >
              {RIVALS[one.rival]} ×{one.seats}
            </button>
          ))}
        </div>
      )}

      {category === null &&
        (overall.length === 0 ? (
          <p className="empty">пока никто не выиграл ни партии — таблица ждёт первого</p>
        ) : (
          <ul className="people">
            {overall.map((row) => (
              <OverallRow key={row.user.id} row={row} onOpenUser={onOpenUser} />
            ))}
          </ul>
        ))}

      {category !== null && (
        <ul className="people">
          {category.leaders.map((row) => (
            <CategoryRowView key={row.user.id} row={row} onOpenUser={onOpenUser} />
          ))}
        </ul>
      )}

      {/*
        Своё место — всегда, а не только когда влез в первые строки: вопрос
        «а я где?» у таблицы лидеров главный.
      */}
      {me !== null ? (
        <p className="muted">
          вы на {me.rank} месте — {me.points} очков, побед {me.won} из {me.played}
        </p>
      ) : (
        overall.length > 0 && (
          <p className="muted">сыграйте партию до победного конца — и у вас появится место</p>
        )
      )}
    </div>
  )
}

/** Строка общего рейтинга: место, человек, очки. */
function OverallRow({ row, onOpenUser }: { row: LeaderRow; onOpenUser: (userId: number) => void }) {
  return (
    <li className="person">
      <span className="leader__rank">{row.rank}</span>
      <Who user={row.user} sub={`побед ${row.won} из ${row.played}`} onOpen={onOpenUser} />
      <span className="leader__points">
        {row.points}
        <small>очков</small>
      </span>
    </li>
  )
}

/** Строка клетки: сортирует доля побед, её и показываем числом справа. */
function CategoryRowView({
  row,
  onOpenUser,
}: {
  row: CategoryRow
  onOpenUser: (userId: number) => void
}) {
  return (
    <li className="person">
      <span className="leader__rank">{row.rank}</span>
      <Who user={row.user} sub={`побед ${row.won} из ${row.played}`} onOpen={onOpenUser} />
      <span className="leader__points">
        {row.winRate}
        <small>% побед</small>
      </span>
    </li>
  )
}

/**
 * Человек в строке — кнопка в его профиль, как в списке друзей: таблица
 * лидеров наполовину состоит из вопроса «а это кто?».
 */
function Who({
  user,
  sub,
  onOpen,
}: {
  user: UserCard
  sub: string
  onOpen: (userId: number) => void
}) {
  const name = user.nickname ?? user.name

  return (
    <button type="button" className="person__who" onClick={() => onOpen(user.id)}>
      <Avatar name={name} src={user.avatar} />
      <span className="person__name">
        <span className="player__name">{name}</span>
        <span className="muted">{sub}</span>
      </span>
    </button>
  )
}

function keyOf(rival: Rival, seats: number): string {
  return `${rival}:${seats}`
}

/*
 * Как называется соперник — те же слова, что в профиле и в меню игры
 * с компьютером: клетку человек должен узнать, а не расшифровать.
 */
const RIVALS: Record<Rival, string> = {
  people: 'люди',
  hard: 'сложный',
  normal: 'средний',
  easy: 'лёгкий',
}
